import { useMemo } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { sitePages as pageOverrides } from '../utils/sitePages';

export const SISTER_ALIAS = 'sister-properties';
export const SISTER_PATH =
  (pageOverrides[SISTER_ALIAS]?.path as string | undefined) ?? '/sister-communities';

/**
 * Peek-only definition for reading SisterProperties community slot groups.
 * Not declared on consumer contentSchemas — SisterProperties owns those
 * editable wrappers. Field shapes must stay aligned with SisterProperties.tsx.
 */
const sisterCommunityPeekDef: SlotGroupDefinition = {
  label: 'Sister Community',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Full Address', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isSisterCommunityFilled(community: Record<string, string | null>): boolean {
  return (
    hasText(community.name) ||
    hasText(community.url) ||
    hasText(community.image) ||
    hasText(community.location)
  );
}

/** Page is eligible when visible and still hosts the SisterProperties section. */
export function isSisterPropertiesPageActive(): boolean {
  const page = (
    pageOverrides as Record<
      string,
      { visible?: boolean; path?: string; sections?: { slot?: string }[] }
    >
  )[SISTER_ALIAS];
  if (!page || page.visible === false) return false;
  return (page.sections ?? []).some((section) => section.slot === 'SisterProperties');
}

/**
 * True when at least one Sister Properties community card has content.
 * Used for thank-you CTA defaults (point at sister properties only when
 * there is something to explore). Nav / sitemap use
 * `isSisterPropertiesPageActive()` instead so the link appears whenever
 * the page itself is on.
 */
export function useHasSisterProperties(): boolean {
  const sister1 = useSlotGroup('sisterproperties.community1', sisterCommunityPeekDef);
  const sister2 = useSlotGroup('sisterproperties.community2', sisterCommunityPeekDef);
  const sister3 = useSlotGroup('sisterproperties.community3', sisterCommunityPeekDef);
  const sister4 = useSlotGroup('sisterproperties.community4', sisterCommunityPeekDef);
  const sister5 = useSlotGroup('sisterproperties.community5', sisterCommunityPeekDef);
  const sister6 = useSlotGroup('sisterproperties.community6', sisterCommunityPeekDef);

  return useMemo(
    () =>
      isSisterPropertiesPageActive() &&
      [sister1, sister2, sister3, sister4, sister5, sister6].some(isSisterCommunityFilled),
    [sister1, sister2, sister3, sister4, sister5, sister6],
  );
}
